import VenueRegistrationFormModal from '../shared/VenueRegistrationFormModal';
import Request, { ApiMode } from '../../../utils/request';
import EventHandler from '../../../utils/dom/event-handler';
import Manipulator from '../../../utils/dom/manipulator';
import { startLoadingAnimation, stopLoadingAnimation } from '../../../utils/dom/loading';
import { VENUE_SELECTION_EXIT_EVENT } from '../formQuestions/VenueSelection';

const DEFAULT_CHECK_IF_WEB_ADMIN_ENDPOINT = '/api/venueregistration/introVenueSelection/CheckIfWebAdmin';
const DEFAULT_VENUE_SUBMIT_ENDPOINT = '/api/venueRegistration/venueConfirmation/venueSubmit';
const DEFAULT_EXIT_REDIRECT_URL = '/pages/household-accounts/advantage-home/advantage-home-page/';

class VenueRegistrationOnboarding extends VenueRegistrationFormModal {
	constructor() {
		super({
			wrapperSelector: '.p-venue-registration-onboarding__content',
			sectionWrapperSelector: null,
			modalSelector: '#js-venue-confirmation-form-modal',
			triggerSelectors: ['.js-venue-registration-onboarding-start'],
			stepsEndpointAttr: 'venue-confirmation-steps-endpoint',
			submitEndpointAttr: 'venue-confirmation-submit-endpoint',
			logPrefix: 'VenueRegistrationOnboarding'
		});

		if (this.sectionWrapper) {
			// Used by `/api/venueRegistration/venueConfirmation/steps`
			// to return either the edit-flow venues or an empty-flow (no venues prefilled).
			this.venueConfirmationFlow = Manipulator.getDataAttribute(
				this.sectionWrapper,
				'venue-confirmation-flow'
			);

			this.venueSubmitEndpoint =
				Manipulator.getDataAttribute(this.sectionWrapper, 'venue-submit-endpoint') ||
				DEFAULT_VENUE_SUBMIT_ENDPOINT;
			this.exitRedirectUrl =
				Manipulator.getDataAttribute(this.sectionWrapper, 'exit-redirect-url') || DEFAULT_EXIT_REDIRECT_URL;
		}
	}

	getRequestParam() {
		return this.venueConfirmationFlow ? { flow: this.venueConfirmationFlow } : {};
	}

	getFormGeneratorOptions() {
		return {
			totalSteps: this.totalSteps,
			hasSeparator: false,
			radioAnswerField: 'id',
			isRenderDependentQuestion: true,
			conditionalStepsVisiblity: true,
			exitButtonLabel: 'Exit',
			exitButtonClasses: 'lta-form-generator__form-exit-button--underlined',
			exitButtonHandler: () => {
				window.location.href = this.exitRedirectUrl || DEFAULT_EXIT_REDIRECT_URL;
			},
			nextStepButtonLabel: 'Save and continue',
			showExitWithoutSavingButton: true,
			exitWithoutSavingButtonLabel: 'Exit without saving',
			exitWithoutSavingButtonHandler: () => this.closeFormModal(),
			submitButton: {
				title: 'Save and continue',
				alwaysVisible: true
			}
		};
	}

	afterFormGeneratorInit() {
		const venueSelectionQuestion = this.formGenerator?.questions?.find(
			(question) => question.questionType === 'venueSelection'
		);
		const venueSelection = venueSelectionQuestion?.ref;

		if (!venueSelection?.setSearchSelectHandler) {
			return;
		}

		venueSelection.setSearchSelectHandler((selectedVenue) =>
			this.checkIfWebAdmin(selectedVenue, venueSelectionQuestion)
		);

		if (venueSelection.setSearchExitRedirectUrl) {
			venueSelection.setSearchExitRedirectUrl(this.exitRedirectUrl || DEFAULT_EXIT_REDIRECT_URL);
		}

		if (venueSelection.setConfirmVenueSubmitHandler) {
			venueSelection.setConfirmVenueSubmitHandler((venue, options) =>
				this.submitConfirmedVenue(venue, options)
			);
		}

		// VenueSelection success-screen Close bubbles this to dismiss the parent form modal too.
		EventHandler.off(this.formModalComponent.contentEl, VENUE_SELECTION_EXIT_EVENT);
		EventHandler.on(this.formModalComponent.contentEl, VENUE_SELECTION_EXIT_EVENT, () => {
			this.closeFormModal();
		});
	}

	/**
	 * Called once after a venue is selected from the search dropdown,
	 * before the blue address card is shown. Endpoint/params come from steps JSON.
	 * When response.isAdmin is true, the selected venue is tagged so the error snackbar can render.
	 */
	async checkIfWebAdmin(selectedVenue = {}, questionData = {}) {
		const endpoint = questionData.onSelectApiEndpoint || DEFAULT_CHECK_IF_WEB_ADMIN_ENDPOINT;
		const paramName = questionData.onSelectApiParamName || 'OrgId';
		const paramFrom = questionData.onSelectApiParamFrom || 'id';
		const responseKey = questionData.onSelectApiResponseKey || 'response';
		const orgId = selectedVenue?.[paramFrom];

		if (!orgId) {
			return selectedVenue;
		}

		try {
			const response = await Request.get(endpoint, {
				[paramName]: orgId,
				...(questionData.onSelectApiAdditionalParams || {})
			});
			const payload = responseKey ? response?.[responseKey] : response;
			return payload ? { ...selectedVenue, ...payload } : selectedVenue;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(`${this.config.logPrefix} – CheckIfWebAdmin error:`, error);
			return selectedVenue;
		}
	}

	/**
	 * Confirm-this-venue (search modal): POST the selected venue to venueSubmit and follow redirect.
	 * @param {Object} venue
	 * @param {{ redirect?: boolean }} [options]
	 * @returns {Promise<boolean>}
	 */
	async submitConfirmedVenue(venue = {}, { redirect = true } = {}) {
		if (!this.venueSubmitEndpoint || !venue || Object.keys(venue).length === 0) {
			return false;
		}

		const venueId = venue.venueId || venue.id || venue.schoolId;

		startLoadingAnimation();
		this.hideError();

		try {
			const response = await Request.post(
				this.venueSubmitEndpoint,
				venueId ? { ...venue, venueId } : { ...venue },
				{
					local: this.config.isLocal,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);

			const { Success, success, errorMessage, redirectUrl, redirectURL } = response || {};
			const nextUrl = redirectUrl || redirectURL;

			stopLoadingAnimation();

			if (errorMessage) {
				this.showError(errorMessage);
				return false;
			}

			if (Success || success) {
				if (redirect && nextUrl) {
					this.goToUrl(nextUrl);
				}

				return true;
			}

			return false;
		} catch (error) {
			stopLoadingAnimation();
			// eslint-disable-next-line no-console
			console.error(`${this.config.logPrefix} – venueSubmit error:`, error?.message || error);
			return false;
		}
	}
}

VenueRegistrationFormModal.boot(VenueRegistrationOnboarding);

export default VenueRegistrationOnboarding;
